import { fieldCategory, minimumConfidence, validateField } from './field-validation'
import { preprocessSheet, type CropSet, type VariantName } from './image-preprocess'
import { recognizeFieldText } from './ocr'
import type { FieldEvidence, SheetAnalysis, SheetDraft } from './types'
import type { ResolvedField } from './analysis'

export type HybridProgress = { stage: string; current: number; total: number; field?: string }
export type FieldCandidate = { value: string; confidence: number; variant: VariantName; valid: boolean; reason?: string }
export type HybridResult = {
  proposal: SheetAnalysis
  evidence: Record<string, FieldEvidence>
  crops: Record<string, Blob>
  candidates: Record<string, FieldCandidate[]>
  preprocessing: { skewDegrees: number; variants: VariantName[] }
}

function ocrMode(path: string) {
  const kind = fieldCategory(path)
  return kind === 'observations' ? 'block' as const : (kind === 'year' || kind === 'sheet' || kind === 'sex' || kind === 'ring') ? 'word' as const : 'line' as const
}

function preferredVariants(path: string): VariantName[] {
  const kind = fieldCategory(path)
  if (kind === 'ring' || kind === 'year' || kind === 'sheet' || kind === 'sex') return ['adaptive', 'contrast', 'perspective']
  return ['contrast', 'perspective', 'adaptive']
}

function appendDead(value: string) {
  if (/\bmuert[oa]\b/i.test(value)) return value
  return value ? `${value}; Muerto` : 'Muerto'
}

export async function analyzeSheetQuick(photo: File, onProgress: (progress: HybridProgress) => void): Promise<HybridResult> {
  onProgress({ stage: 'Corrigiendo orientación, bordes y perspectiva', current: 0, total: 1 })
  const processed = await preprocessSheet(photo)
  const evidence: Record<string, FieldEvidence> = {}
  const crops: Record<string, Blob> = {}
  const candidates: Record<string, FieldCandidate[]> = {}
  let current = 0
  const total = processed.crops.length * 3
  for (const crop of processed.crops) {
    const kind = fieldCategory(crop.path)
    const attempts: FieldCandidate[] = []
    const variants = preferredVariants(crop.path)
    crops[crop.path] = crop.variants.perspective || crop.variants.original!
    for (const variant of variants) {
      const image = crop.variants[variant]
      if (!image) continue
      onProgress({ stage: 'OCR por zonas', current, total, field: crop.path })
      const result = await recognizeFieldText(image, ocrMode(crop.path))
      const checked = validateField(kind, result.text)
      attempts.push({ value: checked.value, confidence: result.confidence, variant, valid: checked.valid, reason: checked.reason })
      current += 1
      if (checked.valid && result.confidence >= minimumConfidence(kind)) break
    }
    attempts.sort((a, b) => (Number(b.valid) * 30 + b.confidence) - (Number(a.valid) * 30 + a.confidence))
    candidates[crop.path] = attempts
    const best = attempts[0] || { value: '', confidence: 0, variant: 'original' as VariantName, valid: false, reason: 'No se ha podido leer' }
    let value = best.value
    if (crop.crossedOut && crop.path.endsWith('.observaciones')) value = appendDead(value)
    const doubtful = !best.valid || best.confidence < minimumConfidence(kind) || (kind === 'observations' && crop.crossedOut)
    evidence[crop.path] = {
      value,
      confidence: best.confidence,
      origin: 'OCR',
      variant: best.variant,
      valid: best.valid,
      doubtful,
      reason: crop.crossedOut ? 'Fila posiblemente tachada; comprobar estado' : doubtful ? best.reason || 'Confianza OCR baja' : undefined
    }
  }

  const value = (path: string) => evidence[path]?.value || null
  const rowIndexes = [...new Set(Object.keys(evidence).flatMap(path => {
    const match = path.match(/^pichones\.(\d+)\./)
    return match ? [Number(match[1])] : []
  }))].sort((a, b) => a - b)
  const rowData = rowIndexes.map(index => ({ index, item: {
    anilla: value(`pichones.${index}.anilla`),
    plumaje: value(`pichones.${index}.plumaje`),
    sexo: value(`pichones.${index}.sexo`),
    observaciones: value(`pichones.${index}.observaciones`)
  }})).filter(row => Object.values(row.item).some(Boolean))
  const rowMap = new Map(rowData.map((row, index) => [row.index, index]))
  const remapPath = (path: string) => path.replace(/^pichones\.(\d+)\./, (match, index) => rowMap.has(Number(index)) ? `pichones.${rowMap.get(Number(index))}.` : match)
  const remapRecord = <T,>(source: Record<string, T>) => Object.fromEntries(Object.entries(source).map(([path, item]) => [remapPath(path), item]))
  const remappedEvidence = remapRecord(evidence)
  const campos_dudosos = Object.entries(remappedEvidence).filter(([, item]) => item.doubtful).map(([campo, item]) => ({ campo, motivo: item.reason || 'Revisar lectura' }))
  return {
    proposal: {
      anio: Number(value('anio')) || null,
      hoja: Number(value('hoja')) || null,
      padre: { nombre: value('padre.nombre'), anilla: value('padre.anilla'), plumaje: value('padre.plumaje') },
      madre: { nombre: value('madre.nombre'), anilla: value('madre.anilla'), plumaje: value('madre.plumaje') },
      pichones: rowData.map(row => row.item),
      campos_dudosos
    },
    evidence: remappedEvidence, crops: remapRecord(crops), candidates: remapRecord(candidates),
    preprocessing: { skewDegrees: processed.skewDegrees, variants: processed.variants }
  }
}

export function mergeResolvedFields(draft: SheetDraft, fields: ResolvedField[]) {
  const next: SheetDraft = {
    ...draft,
    padre: { ...draft.padre }, madre: { ...draft.madre },
    pichones: draft.pichones.map(item => ({ ...item })),
    fieldEvidence: { ...draft.fieldEvidence }
  }
  const setValue = (path: string, value: string) => {
    if (path === 'anio') next.anio = Number(value) || next.anio
    else if (path === 'hoja') next.hoja = Number(value) || next.hoja
    else if (path.startsWith('padre.')) (next.padre as unknown as Record<string, string>)[path.split('.')[1]] = value
    else if (path.startsWith('madre.')) (next.madre as unknown as Record<string, string>)[path.split('.')[1]] = value
    else {
      const match = path.match(/^pichones\.(\d+)\.(.+)$/)
      if (match && next.pichones[Number(match[1])]) (next.pichones[Number(match[1])] as unknown as Record<string, string>)[match[2]] = value
    }
  }
  for (const field of fields) {
    const previous = next.fieldEvidence?.[field.campo]
    const checked = validateField(fieldCategory(field.campo), field.valor || '')
    const disagreement = Boolean(field.valor && previous?.value && previous.value.toLocaleLowerCase('es') !== checked.value.toLocaleLowerCase('es'))
    if (field.valor) setValue(field.campo, checked.value)
    const doubtful = !checked.valid || field.confianza < 70 || disagreement
    next.fieldEvidence![field.campo] = {
      value: checked.value,
      confidence: field.confianza,
      origin: 'IA', valid: checked.valid, doubtful, disagreement,
      reason: disagreement ? `OCR e IA discrepan (OCR: ${previous?.value || 'vacío'})` : field.motivo || (!checked.valid ? checked.reason : field.confianza < 70 ? 'Confianza IA baja' : undefined)
    }
  }
  next.doubtfulFields = Object.entries(next.fieldEvidence || {}).filter(([, item]) => item.doubtful).map(([campo, item]) => ({ campo, motivo: item.reason || 'Revisar lectura' }))
  return next
}
